import React, { useMemo, useState, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { JSONSchema7 } from 'json-schema'
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap'
import classNames from 'classnames'
import jp from 'jsonpath'

import SchemaField from '@rjsf/core/lib/components/fields/SchemaField'
import TextWidget from '@rjsf/core/lib/components/widgets/TextWidget'
import RJSFForm, { FormProps, IChangeEvent } from '@rjsf/core'
import validator from '@rjsf/validator-ajv8'

import {
  ArrayFieldTemplateItemType,
  ArrayFieldTemplateProps,
  FieldTemplateProps,
  ObjectFieldTemplateProps,
  Widget,
  WidgetProps,
  FieldProps,
  getClosestMatchingOption,
  RJSFValidationError,
} from '@rjsf/utils'

import { ReactComponent as LeafIcon } from '../../assets/images/external-contact-update-leaf-field-icon.svg'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'

import {
  ChildStatus,
  DEFAULT_IGNORE_FIELDS,
  DEFAULT_SEARCH_KEY,
  getFieldIdParts,
  getJsonPathFromPathParts,
  getPreviousValue,
  IGNORE_FIELDS,
  LABEL_FIELD_KEY_MAP,
  SEARCH_KEY_MAP,
  generateUiSchemaToHideEMoneyOwnerType,
  SEPARATOR,
  generateUiSchemaToDisableEMoneyFinancialPrioRank,
} from './utils'
import { useEMoneyOwnerSyncedFormData, useFieldInfos } from './hooks'

import { CustomBaseInputTemplate, CustomCheckboxWidget, CustomDateWidget, CustomSelectWidget } from './UpdateExternalContactForm'
import { useSelectContactFormData } from './hooks'

import './UpdateExternalContactFormV2.scss'
import { getIntegrationCategoryDisplayNameByServiceId } from '../../common/integrations'
import { useTracking } from '../../apps/professional-app/fa/common/tracking/hooks'
import { TrackEvent } from '../../apps/professional-app/fa/common/tracking'

const statusVisuals: Record<ChildStatus, { icon: string; color: string }> = {
  success: { icon: 'bi-check-circle-fill', color: 'text-success' },
  error: { icon: 'bi-x-circle-fill', color: 'text-danger' },
}

interface UpdateExternalContactFormV2Props {
  serviceId: string
  rootFieldId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactSchema: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactValues: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  previousContactValues: { [key: string]: any }
  requiredSteps: { [key: string]: string[] }
  metadata: object
  onKeySelected?: (selectedKey: string, isUnselect: boolean) => void
  hidePreviousValues?: boolean
  hideRootFieldHeader?: boolean
}

interface UpdateExternalContactFormContext {
  serviceId: string
  rootFieldId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactValues: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  previousContactValues: { [key: string]: any }
  requiredSteps: { [key: string]: string[] }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: { [key: string]: any }
  onKeySelected?: (selectedKey: string) => void
  hidePreviousValues: boolean
  hideRootFieldHeader: boolean
}

const transformErrors = (errors: RJSFValidationError[]): RJSFValidationError[] => {
  return errors.map((error) => {
    if (error.name === 'if') {
      return { ...error, message: 'You forgot to fill out some of the required fields.' }
    }

    if (error.name === 'oneOf') {
      return { ...error, message: 'Please choose one of the available options to proceed.' }
    }

    if (error.name === 'required') {
      const prop =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error as any)?.params?.missingProperty || error.property?.split('.').pop() || (typeof error.message === 'string' && error.message.split("'")[1])

      if (prop) {
        return { ...error, message: `Please fill out the required '${prop}' field.` }
      } else {
        return error
      }
    }

    if (typeof error.message === 'string' && error.message.toLowerCase().startsWith('must have required property')) {
      const prop = error.message.split("'")[1]
      if (prop) {
        return { ...error, message: `Please fill out the required '${prop}' field.` }
      }
    }

    return error
  })
}

export const UpdateExternalContactFormV2: React.FC<UpdateExternalContactFormV2Props> = ({
  serviceId,
  rootFieldId,
  contactSchema,
  contactValues,
  previousContactValues,
  requiredSteps,
  metadata,
  onKeySelected,
  hidePreviousValues = false,
  hideRootFieldHeader = false,
}) => {
  const { setValue } = useFormContext()

  const formRef = useRef<RJSFForm>(null)
  const [formData, setFormData] = useEMoneyOwnerSyncedFormData(contactValues, contactSchema, serviceId, rootFieldId, setValue)

  const { trackingClient } = useTracking()

  const [editedFields, setEditedFields] = useState<string[]>([])

  const _TextWidget = useMemo(() => wrapWidget(TextWidget), [])
  const _CheckboxWidget = useMemo(() => wrapWidget(CustomCheckboxWidget), [])
  const _SelectWidget = useMemo(() => wrapWidget(CustomSelectWidget), [])
  const _DateWidget = useMemo(() => wrapWidget(CustomDateWidget), [])

  const additionalUiSchema = () => {
    const disableEmoneyFinancialPriosRank = serviceId === 'emoney' ? generateUiSchemaToDisableEMoneyFinancialPrioRank() : {}

    const hideEmoneyOwner = serviceId === 'emoney' ? generateUiSchemaToHideEMoneyOwnerType(contactSchema) : {}
    return { ...hideEmoneyOwner, ...disableEmoneyFinancialPriosRank }
  }

  const formProps: FormProps = {
    validator,
    schema: contactSchema,
    formData,
    uiSchema: {
      'ui:rootFieldId': rootFieldId,
      'ui:submitButtonOptions': {
        norender: true,
      },
      ...additionalUiSchema(),
    },
    templates: {
      BaseInputTemplate: CustomBaseInputTemplate,
      FieldTemplate: CustomFieldTemplate,
      ObjectFieldTemplate: CustomObjectFieldTemplate,
      ArrayFieldTemplate: CustomArrayFieldTemplate,
      ArrayFieldItemTemplate: CustomArrayFieldItemTemplate,
    },
    widgets: {
      TextWidget: _TextWidget,
      CheckboxWidget: _CheckboxWidget,
      SelectWidget: _SelectWidget,
      DateWidget: _DateWidget,
    },
    fields: {
      OneOfField: CustomOneOfField,
    },
    formContext: {
      serviceId,
      rootFieldId,
      contactValues,
      previousContactValues,
      requiredSteps,
      metadata,
      onKeySelected,
      hidePreviousValues,
      hideRootFieldHeader,
    },
    noHtml5Validate: true,
    liveValidate: true,
    showErrorList: false,
    idSeparator: SEPARATOR,
    experimental_defaultFormStateBehavior: {
      emptyObjectFields: 'skipDefaults',
    },
    transformErrors,
  }

  const handleChange = (e: IChangeEvent, id?: string) => {
    setFormData(e.formData)
    if (id !== undefined && !editedFields.includes(id)) {
      trackingClient.track({
        eventName: TrackEvent.CONTACT_UPDATE_RESULTS_EDITED,
        eventDetails: {
          serviceId,
          fieldId: id,
        },
      })
      const updatedEditedFields = [...editedFields, id]
      setEditedFields(updatedEditedFields)
    }
  }

  return <RJSFForm {...formProps} ref={formRef} onChange={handleChange} />
}

const CustomFieldTemplate = (props: FieldTemplateProps) => {
  const { errors, children, id: fieldId, schema, required, registry } = props

  const { rootFieldId, serviceId, contactValues } = registry.formContext as UpdateExternalContactFormContext

  const generatedValue = useMemo(() => {
    const jsonPath = getJsonPathFromPathParts(getFieldIdParts(rootFieldId, fieldId))
    if (!jsonPath) {
      return contactValues
    }
    const value = jp.value(contactValues, jsonPath)
    if (value !== null) {
      return value
    }
  }, [contactValues, fieldId, rootFieldId])

  const isObject = schema.type === 'object'
  const isArray = schema.type === 'array'

  const isConstant = schema.const !== undefined
  const isEmpty = generatedValue === undefined || (isObject && !Object.keys(generatedValue).length) || (isArray && !generatedValue.length)

  const ignoreFields = IGNORE_FIELDS[serviceId] ?? DEFAULT_IGNORE_FIELDS
  const fieldIdParts = fieldId.split(SEPARATOR)
  const shouldIgnore = ignoreFields.includes(fieldIdParts[fieldIdParts.length - 1])

  if (shouldIgnore || (!required && isEmpty) || isConstant) {
    return null
  }

  const isRoot = fieldId === rootFieldId

  return (
    <div
      className={classNames({
        'border border-1 zocks-border-0.25 zocks-border-color-gray-300 p-2': !isRoot && isObject,
      })}
    >
      <FieldHeader {...props} />
      {children}
      {errors}
    </div>
  )
}

const FieldHeader: React.FC<FieldTemplateProps> = (props) => {
  const { id: fieldId, schema, formData, required, registry } = props
  const { rootFieldId, serviceId, contactValues, previousContactValues, requiredSteps, metadata, onKeySelected, hideRootFieldHeader } =
    registry.formContext as UpdateExternalContactFormContext

  const previousValue = useMemo(
    () => getPreviousValue(contactValues, previousContactValues, getFieldIdParts(rootFieldId, fieldId), SEARCH_KEY_MAP[serviceId] ?? DEFAULT_SEARCH_KEY),
    [serviceId, contactValues, fieldId, previousContactValues, rootFieldId],
  )
  const { isSelected, handleSelect } = useSelectContactFormData(serviceId, formData, fieldId.split(SEPARATOR), onKeySelected)

  const isRoot = fieldId === rootFieldId

  const { shouldDisplayStatusIcon, fieldStatus, errorMessages } = useFieldInfos({ fieldId, requiredSteps, metadata })

  if (isRoot) {
    if (hideRootFieldHeader) {
      return (
        <div className='d-flex align-items-center gap-2 p-2'>
          <Form.Check type='checkbox' checked={isSelected} onChange={handleSelect} className='d-flex contact-information-checkbox' />
          <span className='paragraph-medium bold'>All fields</span>
        </div>
      )
    }

    const integrationTypeDisplayName = getIntegrationCategoryDisplayNameByServiceId(serviceId)

    return (
      <div className='d-flex align-items-center gap-2 p-2'>
        <Form.Check type='checkbox' checked={isSelected} onChange={handleSelect} className='d-flex contact-information-checkbox' />
        <div className='p-2 rounded-top rounded-1 current-contact-information w-50'>
          <span className='lead-small'>{integrationTypeDisplayName} Contact Data</span>
        </div>
        <div className='p-2 rounded-top rounded-1 detected-contact-information w-50'>
          <span className='lead-small'>New {integrationTypeDisplayName} Contact Data</span>
        </div>
      </div>
    )
  }

  const isObject = schema.type === 'object'
  const isArray = schema.type === 'array'

  if (!isObject && !isArray) {
    return null
  }

  const { icon, color } = statusVisuals[fieldStatus]
  const statusIcon = <i className={`bi ${icon} ${color}`} />

  const titleSuffix = required ? '*' : ''
  const title = schema.title ? schema.title + titleSuffix : null
  const labelFieldKey = LABEL_FIELD_KEY_MAP[serviceId]
  const labelPrefix = isObject ? (!previousValue ? 'Creating: ' : 'Updating: ') : ''
  const label = formData && formData[labelFieldKey] ? labelPrefix + formData[labelFieldKey] : null

  return (
    <div className='d-flex align-items-center gap-2 p-2'>
      <Form.Check type='checkbox' checked={isSelected} onChange={handleSelect} className='d-flex contact-information-checkbox' />
      {title ? <label className='label-small'>{title}</label> : label ? <label className='lead-small'>{label}</label> : null}
      {errorMessages.length ? (
        <OverlayTrigger placement='top' overlay={<Tooltip id={`tooltip-header-${fieldId}`}>{errorMessages.join(', ')}</Tooltip>}>
          {statusIcon}
        </OverlayTrigger>
      ) : (
        shouldDisplayStatusIcon && statusIcon
      )}
    </div>
  )
}

const CustomObjectFieldTemplate = (props: ObjectFieldTemplateProps) => {
  const { properties, idSchema } = props

  return (
    <div className='d-flex flex-column gap-2'>
      {properties.map((element) => (
        <React.Fragment key={`${idSchema.$id}_${element.name}`}>{element.content}</React.Fragment>
      ))}
    </div>
  )
}

const CustomArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
  const { items, idSchema } = props

  return (
    <div className='d-flex flex-column gap-2'>
      {items.map((element) => (
        <React.Fragment key={`${idSchema.$id}_${element.key}`}>{element.children}</React.Fragment>
      ))}
    </div>
  )
}

const CustomArrayFieldItemTemplate = (props: ArrayFieldTemplateItemType) => {
  return props.children
}

const wrapWidget = (OriginalWidget: Widget) => {
  return (props: WidgetProps) => {
    const { id: fieldId, title, label, required, value, registry } = props

    const { rootFieldId, serviceId, previousContactValues, requiredSteps, metadata, onKeySelected, hidePreviousValues } =
      registry.formContext as UpdateExternalContactFormContext

    const previousValue = useMemo(() => {
      const jsonPath = getJsonPathFromPathParts(getFieldIdParts(rootFieldId, fieldId))
      return previousContactValues ? jp.value(previousContactValues, jsonPath) : null
    }, [previousContactValues, fieldId, rootFieldId])

    const { isSelected, handleSelect } = useSelectContactFormData(serviceId, value, fieldId.split(SEPARATOR), onKeySelected)

    const { shouldDisplayStatusIcon, fieldStatus, errorMessages } = useFieldInfos({ fieldId, requiredSteps, metadata })

    const { icon, color } = statusVisuals[fieldStatus]
    const statusIcon = <i className={`bi ${icon} ${color}`} />

    return (
      <div className='contact-information-field zocks-border-0.25 d-flex gap-2 p-2'>
        <div className='d-flex gap-1 pt-1'>
          <LeafIcon />
          <Form.Check type='checkbox' checked={isSelected} onChange={handleSelect} className='d-flex contact-information-checkbox' />
        </div>
        {!hidePreviousValues ? (
          <>
            <div className='w-50'>
              <Form.Label className='d-flex align-items-center gap-2 label-small'>
                {title ?? label}
                {errorMessages.length ? (
                  <OverlayTrigger placement='top' overlay={<Tooltip id={`tooltip-header-${fieldId}`}>{errorMessages.join(', ')}</Tooltip>}>
                    {statusIcon}
                  </OverlayTrigger>
                ) : (
                  shouldDisplayStatusIcon && statusIcon
                )}
              </Form.Label>
              <OriginalWidget
                {...props}
                id={`${fieldId}_previous`}
                value={previousValue}
                placeholder={'Not set'}
                readonly={true}
                disabled={true}
                onChange={() => {}}
              />
            </div>
            <div className='w-50'>
              <Form.Label className='d-flex align-items-center gap-2 label-small'>
                {title ?? label}
                {required ? '*' : ''}
              </Form.Label>
              <OriginalWidget {...props} />
            </div>
          </>
        ) : (
          <div className='w-100'>
            <Form.Label className='d-flex align-items-center gap-2 label-small'>
              {title ?? label}
              {required ? '*' : ''}
            </Form.Label>
            <OriginalWidget {...props} />
          </div>
        )}
      </div>
    )
  }
}

const CustomOneOfField = (props: FieldProps) => {
  const { schema, onChange, idSchema, registry } = props

  const { rootFieldId, contactValues, previousContactValues, hidePreviousValues } = registry.formContext as UpdateExternalContactFormContext

  const oneOf = useMemo(() => (schema.oneOf as JSONSchema7[]) ?? [], [schema])

  const generatedValue = useMemo(() => {
    const jsonPath = getJsonPathFromPathParts(getFieldIdParts(rootFieldId, idSchema.$id))
    if (!jsonPath) {
      return contactValues
    }
    return jp.value(contactValues, jsonPath)
  }, [contactValues, idSchema.$id, rootFieldId])

  const previousValue = useMemo(() => {
    const jsonPath = getJsonPathFromPathParts(getFieldIdParts(rootFieldId, idSchema.$id))
    if (previousContactValues) {
      return jp.value(previousContactValues, jsonPath)
    } else {
      return null
    }
  }, [previousContactValues, idSchema.$id, rootFieldId])

  const previousSelectedIndex = useMemo(() => getClosestMatchingOption(validator, schema, previousValue, oneOf, -1), [oneOf, previousValue, schema])

  const [selectedIndex, setSelectedIndex] = useState(getClosestMatchingOption(validator, schema, generatedValue, oneOf, -1))

  const options = useMemo(
    () =>
      oneOf
        .filter((e) => typeof e === 'object' && e !== null)
        .map((e, index) => ({
          id: `${index}`,
          label: e.title ?? `Option ${index + 1}`,
        })) ?? [],
    [oneOf],
  )

  const selectedSchema = useMemo(() => {
    return selectedIndex > -1 ? oneOf[selectedIndex] : null
  }, [oneOf, selectedIndex])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function extractConstObject(schema: JSONSchema7): any {
    if (schema.properties && typeof schema.properties === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: Record<string, any> = {}
      for (const [key, propSchema] of Object.entries(schema.properties)) {
        if (typeof propSchema === 'object' && 'const' in propSchema) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          result[key] = (propSchema as any).const
        }
      }
      return result
    }
    return undefined
  }

  const handleOptionSelected = (option: SelectOption) => {
    if (!option || !option.id) {
      return
    }
    const idx = parseInt(option.id)
    if (idx === selectedIndex) {
      return
    }
    setSelectedIndex(idx)

    const newSchema = oneOf[idx]
    if (!newSchema) return

    const selectedValue =
      'const' in newSchema ? newSchema.const : newSchema.enum && newSchema.enum.length === 1 ? newSchema.enum[0] : extractConstObject(newSchema)

    if (selectedValue === undefined) return

    onChange(selectedValue)
  }

  return (
    <div className='d-flex flex-column'>
      <div className='d-flex align-items-center gap-2 p-2'>
        {!hidePreviousValues ? (
          <SelectDropdown
            className={classNames('disabled-contact-information-field border-0', {
              'form-control-color': previousSelectedIndex > -1,
            })}
            placeholder='Not set'
            options={options}
            initialSelectedOption={previousSelectedIndex > -1 ? options[previousSelectedIndex] : null}
            disabled={true}
            hideArrows={true}
          />
        ) : null}
        <SelectDropdown
          placeholder='Not set'
          options={options}
          initialSelectedOption={selectedIndex > -1 ? options[selectedIndex] : null}
          onOptionSelected={handleOptionSelected}
        />
      </div>
      {selectedSchema ? <SchemaField {...props} schema={selectedSchema} /> : null}
    </div>
  )
}
